import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import { Account } from "@common/types/domains/accounting/account";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useAccounts } from "@hooks/useAccounts";
import {
    displayAccountLabel,
    getFallbackAccountRoleForLineItem,
    LineItemFallbackAccountType,
} from "@common/helpers/accounting";
import { findAccountByRole } from "@common/helpers/accrualAccount";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useLedgers } from "@api/ledger.api";
import {
    MatchableItem,
    MatchItemType,
    AccrualMatchTableCallbacks,
} from "../types";
import { useAccrualMatch } from "../useAccrualMatch";
import { AllocationTableOptions } from "../editAllocation/AllocationTable";
import { resolveRemainingRowLedger } from "../editAllocation/remainingRowLedger";

function getFallbackMode(
    type: MatchItemType,
    parentType?: MatchItemType,
): LineItemFallbackAccountType | null {
    switch (type) {
        case MatchItemType.INVOICE:
            return LineItemFallbackAccountType.invoiceAccountsReceivable;
        case MatchItemType.BILL:
            return LineItemFallbackAccountType.billAccountsPayable;
        case MatchItemType.LINE_ITEM:
            if (parentType === MatchItemType.INVOICE) {
                return LineItemFallbackAccountType.invoiceAccountsReceivable;
            }
            if (parentType === MatchItemType.BILL) {
                return LineItemFallbackAccountType.billAccountsPayable;
            }
            return null;
        default:
            return null;
    }
}

type AccountCellMeta = ViewTableMeta<
    AccrualMatchTableCallbacks,
    AllocationTableOptions
>;

/**
 * Which entity's chart of accounts to load for the account dropdown/label.
 * Remaining rows source accounts from table meta. Accrual-basis rows only
 * load when the entity actually has an accrual ledger (else the column is
 * blank); cash-basis rows always have a cash ledger.
 */
function resolveAccountsEntityId({
    isRemaining,
    isAccrualBasis,
    hasAccrualLedger,
    entityId,
}: {
    isRemaining: boolean;
    isAccrualBasis: boolean;
    hasAccrualLedger: boolean;
    entityId: number;
}): number | undefined {
    if (isRemaining) {
        return undefined;
    }
    if (isAccrualBasis) {
        return hasAccrualLedger ? entityId : undefined;
    }
    return entityId;
}

export const AccountCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const { accountIds, entityId, type } = row.original;
        const parentType = row.getParentRow()?.original.type;
        const meta = table.options.meta as AccountCellMeta | undefined;
        const section = meta?.options?.section;
        const isRemaining = section === "remaining";

        // Source entity basis: cash-basis transfer matching shows the cash
        // ledger account, accrual matching shows the accrual ledger account.
        const { ledgerBasis } = useAccrualMatch();
        const isAccrualBasis = ledgerBasis === AccountingLedgerBasis.accruals;

        const showAccountNumbersHook = useShowAccountNumbers();
        const showAccountNumbers =
            meta?.options?.showAccountNumbers ?? showAccountNumbersHook;

        const { data: ledgers } = useLedgers(
            isRemaining ? undefined : entityId || undefined,
        );
        const hasAccrualLedger = useMemo(
            () =>
                ledgers?.some(
                    (l) => l.basis === AccountingLedgerBasis.accruals,
                ) ?? false,
            [ledgers],
        );

        const accountsEntityId = resolveAccountsEntityId({
            isRemaining,
            isAccrualBasis,
            hasAccrualLedger,
            entityId,
        });
        const { accounts: hookAccounts } = useAccounts({
            entityId: accountsEntityId,
            ledgerBasis,
        });

        const remainingLedger = resolveRemainingRowLedger({
            options: meta?.options,
            item: row.original,
        });
        const accounts =
            isRemaining && remainingLedger.accounts
                ? remainingLedger.accounts
                : hookAccounts;
        const effectiveEntityId = remainingLedger.entityId;

        const fallbackMode = getFallbackMode(type, parentType);
        const fallbackAccountId = useMemo(() => {
            if (!fallbackMode) {
                return null;
            }
            const role = getFallbackAccountRoleForLineItem({
                lineItemFallbackAccountType: fallbackMode,
            });
            return findAccountByRole(accounts, role)?.id ?? null;
        }, [fallbackMode, accounts]);

        // Resolve a chart-default account id for a given fallback mode.
        const resolveFallbackId = useCallback(
            (mode: LineItemFallbackAccountType | null): string | null => {
                if (!mode) {
                    return null;
                }
                const role = getFallbackAccountRoleForLineItem({
                    lineItemFallbackAccountType: mode,
                });
                return findAccountByRole(accounts, role)?.id ?? null;
            },
            [accounts],
        );

        // For INVOICE/BILL parent rows, derive the effective Accrual ledger
        // accounts from each line item's AR/AP (with chart fallback for
        // missing values), so the parent reflects "Multiple accounts" when
        // its lines target different accounts.
        // For TRANSACTION rows in the source section, derive from the
        // currently selected matched items' aggregated accounts.
        const collectLineItemAccountIds = useCallback(
            (
                ids: Set<string>,
                lineItemParentType: MatchItemType,
                lineItems: MatchableItem["lineItems"],
            ) => {
                const lineFallback = resolveFallbackId(
                    getFallbackMode(
                        MatchItemType.LINE_ITEM,
                        lineItemParentType,
                    ),
                );
                for (const li of lineItems ?? []) {
                    const id = li.accountId ?? lineFallback;
                    if (id) {
                        ids.add(id);
                    }
                }
            },
            [resolveFallbackId],
        );

        const aggregatedAccountIds = useMemo<string[] | null>(() => {
            if (type === MatchItemType.INVOICE || type === MatchItemType.BILL) {
                const lineItems = row.original.lineItems ?? [];
                if (lineItems.length === 0) {
                    return null;
                }
                const ids = new Set<string>();
                collectLineItemAccountIds(ids, type, lineItems);
                return [...ids];
            }

            if (section === "source" && type === MatchItemType.TRANSACTION) {
                const selectedItems = meta?.options?.selectedItems ?? [];
                if (selectedItems.length === 0) {
                    return null;
                }
                const ids = new Set<string>();
                for (const item of selectedItems) {
                    if (
                        item.type === MatchItemType.INVOICE ||
                        item.type === MatchItemType.BILL
                    ) {
                        collectLineItemAccountIds(
                            ids,
                            item.type,
                            item.lineItems,
                        );
                    } else if (item.type === MatchItemType.TRANSACTION) {
                        for (const id of item.accountIds) {
                            ids.add(id);
                        }
                    }
                }
                return [...ids];
            }

            return null;
        }, [
            type,
            section,
            row.original.lineItems,
            meta?.options?.selectedItems,
            collectLineItemAccountIds,
        ]);

        const effectiveAccountIds = aggregatedAccountIds ?? accountIds;

        const effectiveId = effectiveAccountIds[0] ?? fallbackAccountId;

        const selectedAccount = useMemo(
            () =>
                effectiveId
                    ? (accounts.find((a) => a.id === effectiveId) ?? undefined)
                    : undefined,
            [accounts, effectiveId],
        );

        const label = useMemo(() => {
            if (effectiveAccountIds.length > 1) {
                return "Multiple accounts";
            }
            if (!selectedAccount) {
                return "";
            }
            return displayAccountLabel(selectedAccount, showAccountNumbers);
        }, [effectiveAccountIds, selectedAccount, showAccountNumbers]);

        const multipleAccountsTooltip = useMemo(() => {
            if (effectiveAccountIds.length <= 1) {
                return undefined;
            }
            const labels = effectiveAccountIds
                .map((id) => accounts.find((a) => a.id === id))
                .filter((a): a is Account => a != null)
                .map((a) => displayAccountLabel(a, showAccountNumbers));
            if (labels.length === 0) {
                return undefined;
            }
            return (
                <>
                    {labels.map((accountLabel, i) => (
                        <div key={i}>{accountLabel}</div>
                    ))}
                </>
            );
        }, [effectiveAccountIds, accounts, showAccountNumbers]);

        const onAccountChange = meta?.callbacks?.onRemainingAccountChange;
        const handleChange = useCallback(
            (account: Account | null) => {
                onAccountChange?.(row.original.id, account?.id ?? null);
            },
            [onAccountChange, row.original.id],
        );

        if (isRemaining) {
            return (
                <ViewTableCell captureClicks>
                    <AccountCombobox
                        showAccountCode={showAccountNumbers}
                        selectedAccount={selectedAccount}
                        onChange={handleChange}
                        entityId={effectiveEntityId}
                        ledgerBasis={AccountingLedgerBasis.accruals}
                        trigger={
                            <Picker.CellContent
                                value={selectedAccount ? label : undefined}
                            />
                        }
                    />
                </ViewTableCell>
            );
        }

        if (multipleAccountsTooltip) {
            return (
                <ViewTableCell>
                    <Tooltip content={multipleAccountsTooltip}>
                        <span>{label}</span>
                    </Tooltip>
                </ViewTableCell>
            );
        }

        return <ViewTableCell>{label}</ViewTableCell>;
    },
);
AccountCell.displayName = "AccountCell";
